import { simplifyCartItems } from '@client/domain/checkout';
import { useApiClient } from '@client/hooks/useApiClient';
import { useOrderStatusPoll } from '@client/hooks/useOrderStatusPoll';
import { CheckoutType, useCheckoutStatus, } from '@client/providers/CheckoutStatusProvider';
import { useShoppingCart } from '@client/providers/ShoppingCartProvider';
import { toastError } from '@client/toaster';
import { callApi } from '@client/utils/apiUtils';
import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useRef, useState } from 'react';
const PAYMENT_REQUEST_LABEL = 'Heirloom';
const SHIPPING_OPTION_ID = 'standard';
const buildShippingOptions = (shippingTotal) => [
    {
        id: SHIPPING_OPTION_ID,
        label: 'Shipping',
        detail: '',
        amount: shippingTotal,
    },
];
const buildDisplayItems = (itemPriceTotal, taxCents) => [
    { label: 'Subtotal', amount: itemPriceTotal },
    { label: 'Tax', amount: taxCents },
];
const splitRecipientName = (recipient) => {
    const [firstName = '', ...rest] = (recipient ?? '')
        .trim()
        .split(/\s+/);
    return { firstName, lastName: rest.join(' ') || firstName };
};
export const useApplePayCheckout = (onClose) => {
    const stripe = useStripe();
    const apiClient = useApiClient();
    const { pollUntilPaid } = useOrderStatusPoll();
    const { items, itemPriceTotal, shippingTotal } = useShoppingCart();
    const { startSubmitting, startConfirming, settle, triggerTimeout, } = useCheckoutStatus();
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [available, setAvailable] = useState(false);
    const cartRef = useRef({
        items: simplifyCartItems(items),
        itemPriceTotal,
        shippingTotal,
    });
    const taxCentsRef = useRef(0);
    useEffect(() => {
        cartRef.current = {
            items: simplifyCartItems(items),
            itemPriceTotal,
            shippingTotal,
        };
    }, [items, itemPriceTotal, shippingTotal]);
    useEffect(() => {
        if (!stripe)
            return;
        const pr = stripe.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: {
                label: PAYMENT_REQUEST_LABEL,
                amount: itemPriceTotal + shippingTotal,
            },
            displayItems: buildDisplayItems(itemPriceTotal, 0),
            shippingOptions: buildShippingOptions(shippingTotal),
            requestPayerName: true,
            requestPayerEmail: true,
            requestShipping: true,
            disableWallets: ['googlePay', 'link', 'browserCard'],
        });
        pr.canMakePayment().then((result) => {
            setAvailable(!!result?.applePay);
        });
        pr.on('shippingaddresschange', async (event) => {
            const shippingAddress = {
                firstName: '',
                lastName: '',
                line1: '',
                line2: '',
                city: event.shippingAddress.city ?? '',
                state: event.shippingAddress.region ?? '',
                zip: event.shippingAddress.postalCode ?? '',
            };
            const result = await callApi(apiClient.checkout.calculateTax({
                body: {
                    items: cartRef.current.items,
                    shippingAddress,
                },
            }));
            if (result.error !== null) {
                event.updateWith({ status: 'fail' });
                return;
            }
            taxCentsRef.current = result.data.TaxTotalCents;
            const current = cartRef.current;
            event.updateWith({
                status: 'success',
                total: {
                    label: PAYMENT_REQUEST_LABEL,
                    amount: current.itemPriceTotal +
                        current.shippingTotal +
                        result.data.TaxTotalCents,
                },
                displayItems: buildDisplayItems(current.itemPriceTotal, result.data.TaxTotalCents),
                shippingOptions: buildShippingOptions(current.shippingTotal),
            });
        });
        pr.on('paymentmethod', async (event) => {
            startSubmitting(CheckoutType.ApplePay);
            try {
                const address = event.shippingAddress;
                const { firstName, lastName } = splitRecipientName(address?.recipient);
                const shippingAddress = {
                    firstName,
                    lastName,
                    line1: address?.addressLine?.[0] ?? '',
                    line2: address?.addressLine?.[1] ?? '',
                    city: address?.city ?? '',
                    state: address?.region ?? '',
                    zip: address?.postalCode ?? '',
                };
                const current = cartRef.current;
                const totalCents = current.itemPriceTotal +
                    current.shippingTotal +
                    taxCentsRef.current;
                const submitResult = await callApi(apiClient.checkout.submitOrder({
                    body: {
                        items: current.items,
                        email: event.payerEmail ?? '',
                        shippingAddress,
                        totalCents,
                    },
                }));
                if (submitResult.status === 409) {
                    event.complete('fail');
                    settle();
                    toastError('Prices have changed', 'Please refresh the page and try again.');
                    return;
                }
                if (submitResult.error !== null) {
                    event.complete('fail');
                    settle();
                    toastError('Failed to submit order', submitResult.error);
                    return;
                }
                const { clientSecret, orderShortId, accessKey } = submitResult.data;
                const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(clientSecret, { payment_method: event.paymentMethod.id }, { handleActions: false });
                if (confirmError) {
                    event.complete('fail');
                    settle();
                    toastError('Payment failed', confirmError.message ??
                        'Please check your payment details and try again.');
                    return;
                }
                event.complete('success');
                if (paymentIntent?.status === 'requires_action') {
                    const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
                    if (actionError) {
                        settle();
                        toastError('Payment failed', actionError.message ??
                            'Please check your payment details and try again.');
                        return;
                    }
                }
                startConfirming();
                pollUntilPaid(orderShortId, accessKey, {
                    onSuccess: () => {
                        settle();
                        onClose();
                    },
                    onTimeout: triggerTimeout,
                });
            }
            catch {
                event.complete('fail');
                settle();
                toastError('Failed to submit order', 'Something went wrong processing your payment. Please try again.');
            }
        });
        setPaymentRequest(pr);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripe]);
    useEffect(() => {
        if (!paymentRequest)
            return;
        paymentRequest.update({
            total: {
                label: PAYMENT_REQUEST_LABEL,
                amount: itemPriceTotal +
                    shippingTotal +
                    taxCentsRef.current,
            },
            displayItems: buildDisplayItems(itemPriceTotal, taxCentsRef.current),
            shippingOptions: buildShippingOptions(shippingTotal),
        });
    }, [paymentRequest, itemPriceTotal, shippingTotal]);
    return {
        paymentRequest,
        available,
    };
};
